import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISO_DATE_TIME_FORMAT,
  TypeGuard,
  toTimestamp,
  type Timestamp,
  type Timezone,
} from '@principle-theorem/shared';
import { flow, isNumber, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';

export const PATIENT_IMAGE_RESOURCE_TYPE = 'patientImage';

export const PATIENT_IMAGE_SOURCE_ENTITY: ISourceEntity = SourceEntity.init({
  metadata: {
    label: 'Patient Image List',
    description: '',
    idPrefix: PATIENT_IMAGE_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface IPraktikaPatientImage {
  created_at: string;
  patient_id: number;
  filename: string;
  md5: string;
  size_in_bytes: number;
}

export function isPraktikaPatientImage(
  item: unknown,
  logError: boolean
): item is IPraktikaPatientImage {
  return TypeGuard.interface<IPraktikaPatientImage>(
    {
      created_at: isString,
      patient_id: isNumber,
      filename: isString,
      md5: isString,
      size_in_bytes: isNumber,
    },
    logError
  )(item);
}

export interface IPraktikaPatientImageTranslations {
  createdAt: Timestamp;
}

export interface IPraktikaPatientImageFilters {
  patientId: string;
  createdAt: Timestamp;
}

const PATIENT_IMAGE_SOURCE_QUERY = `
SELECT
  convert_to_integer(ipatientnumber) AS patient_id,
  dtcreated AS created_at,
  vchmd5 AS md5,
  convert_to_integer(ifilesizebytes) AS size_in_bytes,
  vchuserfilename AS filename
FROM patient_images
WHERE convert_to_integer(ipatientnumber) IS NOT NULL
`;

export type PraktikaPatientImage = ISourceEntityTypes<
  IPraktikaPatientImage,
  IPraktikaPatientImageTranslations,
  IPraktikaPatientImageFilters
>;

export class PatientImageSourceEntity extends BaseSourceEntity<PraktikaPatientImage> {
  sourceEntity = PATIENT_IMAGE_SOURCE_ENTITY;
  entityResourceType = PATIENT_IMAGE_RESOURCE_TYPE;
  sourceQuery = PATIENT_IMAGE_SOURCE_QUERY;
  verifySourceFn = isPraktikaPatientImage;
  override transformDataFn = flow([]);

  translate(
    appointment: IPraktikaPatientImage,
    timezone: Timezone
  ): IPraktikaPatientImageTranslations {
    return {
      createdAt: toTimestamp(
        moment.tz(appointment.created_at, ISO_DATE_TIME_FORMAT, timezone)
      ),
    };
  }

  getSourceRecordId(data: IPraktikaPatientImage): string {
    const fileName = data.filename.includes('/')
      ? data.filename.replace(/^.*\/\s*/, '')
      : data.filename;
    return `${data.patient_id}-${data.created_at}-${fileName}`;
  }

  getSourceLabel(data: IPraktikaPatientImage): string {
    return this.getSourceRecordId(data);
  }

  getFilterData(
    data: IPraktikaPatientImage,
    timezone: Timezone
  ): IPraktikaPatientImageFilters {
    return {
      patientId: data.patient_id.toString(),
      createdAt: toTimestamp(
        moment.tz(data.created_at, ISO_DATE_TIME_FORMAT, timezone)
      ),
    };
  }
}
